import React, { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { screeningGetApi } from '../../../services/screeningApiService';
import { useAppStore } from '../../../store/index';
import { API_URLS, batchPollingInterval, SCREENING_COMPLETE_MESSAGE, SCREENING_FAILED_MESSAGE } from '../../../utils';

export const getIdsToPoll = (data: Record<string, any>[]) => {
    const currentInProgressIds: number[] = [];
    const completedOrFailedIds: { id: number; status: string; fileName: string }[] = [];

    data.forEach((item: Record<string, any>) => {
        if (item.status === 'Processing') {
            currentInProgressIds.push(item.batchId);
        } else if (item.status === 'Completed' || item.status === 'Failed') {
            completedOrFailedIds.push({ id: item.batchId, status: item.status, fileName: item.fileName });
        }
    });

    return { currentInProgressIds, completedOrFailedIds };
};

const BatchPollingManager: React.FC = () => {
    const queryClient = useQueryClient();
    const isPollingActive = useRef(false);

    const {
        triggerBatchPolling,
        setTriggerBatchPolling,
        startPolling,
        setInProgressIds,
        clearInProgressIds,
        inProgressIds,
        showAlert,
        stopPolling,
        removeInProgressIds
    } = useAppStore();

    const startBatchPolling = () => {
        if (isPollingActive.current) {
            return;
        }
        isPollingActive.current = true;

        startPolling({
            id: 'postBatchStatus',
            apiCall: () =>
                screeningGetApi({
                    url: API_URLS.postBatchStatus
                }),
            interval: batchPollingInterval,
            onSuccess: (responseData) => {
                const updatedStore = useAppStore.getState();
                const updatedInProgressIds = updatedStore.inProgressIds;

                const { completedOrFailedIds } = getIdsToPoll(responseData?.data?.data || []);

                const finishedBatches = completedOrFailedIds.filter((item) => updatedInProgressIds.includes(item.id));
                if (finishedBatches.length > 0) {
                    const finishedIds: number[] = [];

                    finishedBatches.forEach((batch) => {
                        const { status, fileName, id } = batch;
                        finishedIds.push(id);

                        if (status === 'Completed') {
                            showAlert({
                                type: 'success',
                                message: SCREENING_COMPLETE_MESSAGE(fileName)
                            });
                        } else if (status === 'Failed') {
                            showAlert({
                                type: 'danger',
                                message: SCREENING_FAILED_MESSAGE(fileName)
                            });
                        }
                    });
                    removeInProgressIds(finishedIds);

                    if (finishedIds.length > 0) {
                        queryClient?.invalidateQueries({ queryKey: [['BatchScreeningListData']] });
                    }
                }

                // Don't check for stopping here - let the effect handle it
            },
            onError: (error) => {
                console.error('Polling error:', error);
            }
        });
    };

    // Effect to handle stopping polling when inProgressIds becomes empty
    useEffect(() => {
        if (isPollingActive.current && inProgressIds.length === 0) {
            stopPolling('postBatchStatus');
            clearInProgressIds();
            setTriggerBatchPolling(false);
            isPollingActive.current = false;
        }
    }, [inProgressIds.length]); // React to changes in inProgressIds length

    // Start polling when component mounts (check for existing batches)
    useEffect(() => {
        const initializePolling = async () => {
            try {
                const response = await screeningGetApi({
                    url: API_URLS.postBatchStatus
                });

                if (response?.data?.data) {
                    const { currentInProgressIds } = getIdsToPoll(response.data.data);

                    if (currentInProgressIds.length > 0) {
                        setInProgressIds(currentInProgressIds);
                    } else {
                        clearInProgressIds();
                    }
                }
            } catch (error) {
                console.error('Error checking for existing batches:', error);
            }
        };

        initializePolling();
    }, []); // Only run on mount

    // Start polling when triggerBatchPolling becomes true OR when new IDs are added
    useEffect(() => {
        if (triggerBatchPolling && inProgressIds.length > 0) {
            queryClient?.invalidateQueries({ queryKey: [['BatchScreeningListData']] });
            startBatchPolling();
        } else if (inProgressIds.length > 0 && !isPollingActive.current) {
            startBatchPolling();
        }
    }, [triggerBatchPolling, inProgressIds.length]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (isPollingActive.current) {
                stopPolling('postBatchStatus');
                isPollingActive.current = false;
            }
        };
    }, []);

    return null;
};
export default BatchPollingManager;
